import { useState } from 'react';

import type { ApiException } from '@/common/error';
import { Avatar, Modal } from '@/components';
import { FormButtonOutlined } from '@/components/FormInputs/FormInputs';
import { useAssignSeatMutation } from '@/store/people/people.store';

import s from '../subscription.module.scss';
import { SEAT_POOL_LABEL, displayName, roleLabel } from '../subscription.seats';
import type { SeatPerson } from '../subscription.seats';
import type { SeatPoolState } from '../useVesselSeats';

const AVATAR_SIZE = 34;

/**
 * Every way this can fail carries the same error `type` and the body has no message field,
 * so the copy has to cover the causes rather than name one. Capacity, eligibility and the
 * caller's rights are all checked before the button is offered, which leaves a stale list —
 * someone else took the seat, or the plan changed underneath — as the realistic cause.
 */
const errorMessage = (error: unknown) =>
  (error as ApiException | null)?.type === 'NOT_FOUND'
    ? 'That person is no longer on this vessel.'
    : 'Could not assign the seat. It may have just been taken, or the plan changed — reopen to see where things stand.';

/** A candidate row. Everyone listed holds no paid seat, so their role is all there is to say. */
function CandidateButton({
  person,
  onClick,
  disabled,
}: {
  person: SeatPerson;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button type="button" className={s.candidate} onClick={onClick} disabled={disabled}>
      <Avatar
        src={person.avatar}
        firstName={person.firstName}
        lastName={person.lastName}
        size={AVATAR_SIZE}
      />
      <span className={s.candidateText}>
        <span className={s.candidateName}>{displayName(person)}</span>
        <span className={s.candidateRole}>{roleLabel(person.role)}</span>
      </span>
    </button>
  );
}

function AssignSeatForm({
  state,
  vesselName,
  serviceId,
  onClose,
}: {
  state: SeatPoolState;
  vesselName: string;
  serviceId: string;
  onClose: () => void;
}) {
  // Which row is in flight, so only that one shows as busy.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const assign = useAssignSeatMutation();

  const { pool, seatType, candidates, occupants, capacity, heldForOwner } = state;
  // The seat kept for an owner still to come is not one of these: it is paid for and empty,
  // and it is the one seat nobody here can be put in.
  const free = Math.max(0, capacity - occupants.length - heldForOwner);

  const pick = (person: SeatPerson) => {
    if (!seatType) return;
    setPendingId(person.personId);
    assign.mutate(
      { personId: person.personId, serviceId, seatType },
      {
        // Closing on success lets the invalidated queries repaint the row underneath.
        onSuccess: onClose,
        onSettled: () => setPendingId(null),
      },
    );
  };

  return (
    <div className={s.modalBody}>
      <div className={s.summaryRow}>
        <span>{vesselName}</span>
        <strong>
          {free} {SEAT_POOL_LABEL[pool].toLowerCase()} {free === 1 ? 'seat' : 'seats'} open
        </strong>
      </div>

      <p className={s.formHint}>
        {pool === 'management'
          ? 'Guests and vendors cannot hold a management seat. '
          : 'Guests cannot hold a crew seat. '}
        Anyone already on a seat is left out — empty their seat first to move them.
      </p>

      <div className={s.candidates}>
        {candidates.map((person) => (
          <CandidateButton
            key={person.personId}
            person={person}
            onClick={() => pick(person)}
            disabled={!!pendingId}
          />
        ))}
      </div>

      {assign.isError && <p className={s.formError}>{errorMessage(assign.error)}</p>}

      <div className={s.modalActions}>
        <FormButtonOutlined onClick={onClose} disabled={!!pendingId}>
          Close
        </FormButtonOutlined>
      </div>
    </div>
  );
}

/**
 * Picks who fills a free seat. Assigning is a single click on a person — there is nothing
 * else to collect, so there is no confirm step.
 */
export function AssignSeatModal({
  open,
  state,
  vesselName,
  serviceId,
  onClose,
}: {
  open: boolean;
  state: SeatPoolState;
  vesselName: string;
  serviceId: string;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign a ${SEAT_POOL_LABEL[state.pool].toLowerCase()} seat`}
    >
      {/* Mounted only while open, so the candidate list is the one that was just computed. */}
      <AssignSeatForm
        state={state}
        vesselName={vesselName}
        serviceId={serviceId}
        onClose={onClose}
      />
    </Modal>
  );
}
